import React from 'react';
import { observer } from 'mobx-react';
import { CenterColumnLayout } from 'components/layouts/center-column-layout';
import { LearnDashboardHeading } from './dashboard-heading';
import { QuickLinks } from 'components/account/quick-links';
import { MediumGap, LargeGap } from '@naan/primitives/spacer/gaps';
import { SoundbiteEtAlWidgetGroup } from './soundbite-et-al-widget-group';
import { DeepScrollRestoration } from 'lib/scroll-utils/deep-scroll-restoration';
import { useLocation, useNavigate } from 'react-router-dom';
import { SampleStories } from './sample-stories';
import { FeaturedContent } from './featured-content';
import { TrialWelcomeMessage } from '../welcome-message/trial-welcome-message';
import { subscribePath } from 'components/nav/path-helpers';
import { TrialDashboardNav } from './trial-dashboard-nav';
import { Button } from '@naan/primitives/button';
import { styled } from '@naan/stitches.config';
import { getFirstScrollingParent } from 'lib/scroll-utils';
import { NewsletterDialog } from 'components/account/newsletter-dialog';
import { DashboardFooter } from './dashboard-footer';

import __ from '@core/lib/localization';

const SubscriberHeading = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  padding: '20px 0',
  borderBottom: '1px solid $black-alpha-10',
  gap: 12,
  marginBottom: 24,
  position: 'sticky',
  top: 0,
  backgroundColor: '$red-50',
  zIndex: '1',
  '& .heading': {
    textStyle: 'medium-heading',
  },
  '@medium': {
    marginBottom: 40,
    '& .heading': {
      textStyle: 'large-heading',
    },
  },
});

const maybeScrollToFragment = () => {
  const fragment = window.location.hash;
  if (fragment) {
    const element = document.getElementById(fragment.replace('#', ''));

    if (element) {
      const scrollingContainer = getFirstScrollingParent(element);
      if (scrollingContainer) {
        scrollingContainer.scrollTo({
          top: element.offsetTop - scrollingContainer.offsetTop - 56,
          behavior: 'smooth',
        });
      }
    }
  }
};

export const TrialDashboard = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    requestAnimationFrame(maybeScrollToFragment);
  }, [location]);

  return (
    <>
      <DeepScrollRestoration pageKey={location.pathname} />
      <CenterColumnLayout>
        <TrialDashboardNav />
        <LearnDashboardHeading />
        <TrialWelcomeMessage />
        <SoundbiteEtAlWidgetGroup />
        <MediumGap />
        <SampleStories />
        <MediumGap />
      </CenterColumnLayout>
      <CenterColumnLayout backgroundColor="#ffeded">
        <SubscriberHeading>
          <h3 className="heading">
            {__('Available to subscribers', 'dashboard.availableToSubscribers')}
          </h3>
          <Button
            presentation={'teal'}
            label={__('Learn more', 'common.learnMore')}
            size={'medium'}
            onClick={() => {
              navigate(subscribePath());
            }}
          />
        </SubscriberHeading>
        <FeaturedContent />
        <LargeGap />
      </CenterColumnLayout>
      <CenterColumnLayout>
        <LargeGap />
        <QuickLinks />
        <LargeGap />
        <DashboardFooter />
      </CenterColumnLayout>
      <NewsletterDialog />
    </>
  );
});
